import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

type InquiryKey = 'system' | 'app' | 'ad' | 'other';

type FormState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
};

const inquiryOptions: { key: InquiryKey; label: string; icon: string }[] = [
  { key: 'system', label: 'システム開発について', icon: 'ri-code-s-slash-line' },
  { key: 'app', label: 'アプリ制作について', icon: 'ri-smartphone-line' },
  { key: 'ad', label: '広告運用について', icon: 'ri-megaphone-line' },
  { key: 'other', label: 'その他', icon: 'ri-question-line' },
];

const initialFormState: FormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  message: '',
};

const initialChecks: Record<InquiryKey, boolean> = {
  system: false,
  app: false,
  ad: false,
  other: false,
};

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [checks, setChecks] = useState<Record<InquiryKey, boolean>>(initialChecks);
  const [agreed, setAgreed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ status: 'success' | 'error'; message: string } | null>(null);

  const selectedInquiryLabels = inquiryOptions
    .filter((option) => checks[option.key])
    .map((option) => option.label);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCheck = (key: InquiryKey) => {
    setChecks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const validateForm = () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      alert('必須項目を入力してください。');
      return false;
    }

    if (selectedInquiryLabels.length === 0) {
      alert('お問い合わせ種別を1つ以上選択してください。');
      return false;
    }

    if (!agreed) {
      alert('プライバシーポリシーへの同意が必要です。');
      return false;
    }

    return true;
  };

  const handleConfirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    setSending(true);

    const params = new URLSearchParams();
    params.set('name', form.name.trim());
    params.set('company', form.company.trim());
    params.set('email', form.email.trim());
    params.set('phone', form.phone.trim());
    params.set('inquiry_type', selectedInquiryLabels.join('、'));
    params.set('message', form.message.trim());
    params.set('privacy', agreed ? '同意済み' : '未同意');

    try {
      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const text = (await response.text()).trim();

      if (!response.ok || text !== 'OK') {
        throw new Error('send_failed');
      }

      setForm(initialFormState);
      setChecks(initialChecks);
      setAgreed(false);
      setResult({
        status: 'success',
        message: 'お問い合わせを受け付けました。2〜3営業日以内にご連絡いたします。',
      });
    } catch {
      setResult({
        status: 'error',
        message: '送信に失敗しました。時間をおいて再度お試しください。',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      <Navbar />
      <section className="relative pt-36 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20colorful%20pink%20green%20emerald%20gradient%20glass%20crystal%20background%20soft%20bokeh%20light%20particles%20fresh%20peach%20tones%20modern%20IT%20contact%20form%20background%20ultra%20high%20quality%20artistic&width=1920&height=1080&seq=contact001&orientation=landscape"
            alt="contact bg"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-pink-100/80 via-white/70 to-white"></div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto px-6">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl border border-pink-100 p-8 md:p-12">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-black text-gray-800 mb-2">お問い合わせ</h1>
              <p className="text-gray-500 text-sm">お気軽にご相談ください。2〜3営業日以内にご返信いたします。</p>
            </div>

            <form onSubmit={handleConfirm} className="flex flex-col gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  お名前 <span className="text-pink-400">*</span>
                </label>
                <input
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleInputChange}
                  placeholder="山田 太郎"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-pink-300 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">会社名・屋号</label>
                <input
                  name="company"
                  type="text"
                  value={form.company}
                  onChange={handleInputChange}
                  placeholder="株式会社〇〇 / 個人事業主の方は空欄可"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-pink-300 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  メールアドレス <span className="text-pink-400">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="example@peach.co.jp"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-pink-300 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">電話番号</label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleInputChange}
                  placeholder="06-0000-0000"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-pink-300 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  お問い合わせ種別 <span className="text-pink-400">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {inquiryOptions.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => toggleCheck(item.key)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all cursor-pointer ${
                        checks[item.key]
                          ? 'border-pink-400 bg-pink-50 text-pink-600'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-pink-200'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 flex items-center justify-center rounded border-2 transition-all ${
                          checks[item.key] ? 'border-pink-400 bg-pink-400' : 'border-gray-300'
                        }`}
                      >
                        {checks[item.key] && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <i className={`${item.icon} text-base`}></i>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  お問い合わせ内容 <span className="text-pink-400">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={form.message}
                  onChange={handleInputChange}
                  placeholder="ご要望・ご質問をご記入ください（500文字以内）"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-pink-300 bg-white resize-none"
                />
                <p className={`text-xs mt-1 text-right ${form.message.length >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
                  {form.message.length}/500
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(event) => setAgreed(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-pink-400"
                />
                <span className="text-sm text-gray-600">
                  <Link to="/privacy" className="text-pink-500 underline hover:text-pink-600">
                    プライバシーポリシー
                  </Link>
                  に同意する
                </span>
              </label>

              <button
                type="submit"
                disabled={sending}
                className="whitespace-nowrap cursor-pointer w-full bg-gradient-to-r from-pink-400 to-rose-500 text-white font-semibold py-4 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                <i className="ri-file-list-3-line"></i>
                入力内容を確認する
              </button>
            </form>
          </div>
        </div>
      </section>

      {confirmOpen && (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-pink-100 p-6 md:p-8">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <p className="text-xs font-semibold tracking-widest text-pink-400 uppercase mb-2">Confirm</p>
                <h2 className="text-2xl font-black text-gray-800">入力内容の確認</h2>
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="w-10 h-10 rounded-full border border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
                aria-label="閉じる"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4 text-sm">
              {[
                { label: 'お名前', value: form.name },
                { label: '会社名・屋号', value: form.company || '未入力' },
                { label: 'メールアドレス', value: form.email },
                { label: '電話番号', value: form.phone || '未入力' },
                { label: 'お問い合わせ種別', value: selectedInquiryLabels.join('、') },
                { label: 'お問い合わせ内容', value: form.message },
              ].map((item) => (
                <div key={item.label} className="border border-gray-100 rounded-xl p-4">
                  <p className="text-xs font-semibold tracking-wide text-gray-400 mb-2">{item.label}</p>
                  <p className="text-gray-700 whitespace-pre-line break-words">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="whitespace-nowrap cursor-pointer flex-1 border border-gray-200 text-gray-700 font-semibold py-3 rounded-full hover:bg-gray-50 transition-colors"
              >
                戻って修正する
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="whitespace-nowrap cursor-pointer flex-1 bg-gradient-to-r from-pink-400 to-rose-500 text-white font-semibold py-3 rounded-full hover:opacity-90 transition-opacity"
              >
                この内容で送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {sending && (
        <div className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-pink-100 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-pink-50 flex items-center justify-center mx-auto mb-5">
              <i className="ri-loader-4-line animate-spin text-2xl text-pink-500"></i>
            </div>
            <h2 className="text-xl font-black text-gray-800 mb-2">送信中です</h2>
            <p className="text-sm text-gray-500">内容を送信しています。しばらくお待ちください。</p>
          </div>
        </div>
      )}

      {result && (
        <div className="fixed inset-0 z-[90] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-pink-100 p-8 text-center">
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 ${
                result.status === 'success' ? 'bg-emerald-50' : 'bg-red-50'
              }`}
            >
              <i
                className={`text-2xl ${
                  result.status === 'success' ? 'ri-check-line text-emerald-500' : 'ri-close-line text-red-500'
                }`}
              ></i>
            </div>
            <h2 className="text-xl font-black text-gray-800 mb-2">
              {result.status === 'success' ? '送信完了しました' : '送信に失敗しました'}
            </h2>
            <p className="text-sm text-gray-500 mb-6 whitespace-pre-line">{result.message}</p>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="whitespace-nowrap cursor-pointer w-full bg-gradient-to-r from-pink-400 to-rose-500 text-white font-semibold py-3 rounded-full hover:opacity-90 transition-opacity"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
